import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'wouter';
import { getSlugFromPath, stripLocaleFromPath } from '@/i18n/locale-utils';
// seoMeta is plain JS shared with the build-time prerender generator.
import { seoRoutes, ogLocaleFor, resolveMeta, DEFAULT_TITLE, DEFAULT_DESC } from '@/lib/seoMeta';

function setMeta(selector: string, attr: 'name' | 'property', key: string, value: string) {
  let el = document.head.querySelector<HTMLMetaElement>(selector);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', value);
}

/**
 * Runtime <head> manager: sets a unique, localized <title> + description (and the
 * matching OG/Twitter tags) for each static marketing route, derived from the
 * page's already-translated hero strings. Routes not in the map fall back to the
 * default shell metadata. Only blog POST detail pages manage their own title —
 * everything else, the BYO-LLM guides included, resolves through seoRoutes so
 * the prerendered and runtime <head> stay identical.
 */
export function SeoHead() {
  const [location] = useLocation();
  const { t, i18n } = useTranslation();
  // wouter's location is locale-stripped by LocaleRouter, but read the real path
  // for the locale so OG locale stays correct regardless of routing base.
  const slug = getSlugFromPath(window.location.pathname);
  const subpath = stripLocaleFromPath(location).replace(/^\/+|\/+$/g, '');

  useEffect(() => {
    // Blog POST detail pages (/blog/:slug) manage their own localized title,
    // description, OG tags and BlogPosting JSON-LD in BlogPost.tsx — don't touch
    // them here or we'd clobber that with the default shell meta. The /blog
    // listing (subpath === "blog") is still handled below.
    if (subpath.startsWith('blog/')) return;

    // Resolve with the shared helper — i18n's t() returns the key itself when a
    // string is missing, so map that back to null for resolveMeta.
    const resolved = resolveMeta(seoRoutes[subpath], slug || 'en', (k) => {
      const v = t(k);
      return v && v !== k ? v : null;
    });
    const title = resolved.title || DEFAULT_TITLE;
    const description = resolved.description || DEFAULT_DESC;

    document.title = title;
    setMeta('meta[name="description"]', 'name', 'description', description);
    setMeta('meta[property="og:title"]', 'property', 'og:title', title);
    setMeta('meta[property="og:description"]', 'property', 'og:description', description);
    setMeta('meta[name="twitter:title"]', 'name', 'twitter:title', title);
    setMeta('meta[name="twitter:description"]', 'name', 'twitter:description', description);

    const ogLocale = (ogLocaleFor as Record<string, string>)[slug || 'en'] || 'en_US';
    setMeta('meta[property="og:locale"]', 'property', 'og:locale', ogLocale);

    // og:url + twitter:url -> this page's canonical (fixed apex origin, trailing
    // slash) so they don't stay pinned to the homepage on SPA navigation.
    const path = window.location.pathname;
    const canonicalUrl = 'https://traverba.com' + (path.endsWith('/') ? path : path + '/');
    setMeta('meta[property="og:url"]', 'property', 'og:url', canonicalUrl);
    setMeta('meta[name="twitter:url"]', 'name', 'twitter:url', canonicalUrl);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subpath, slug, i18n.language]);

  return null;
}
